/**
 * Single owner of the `messages:{companyId}` realtime topic.
 *
 * Two supabase-js channels sharing one topic name causes a subscribe/rejoin
 * loop that can saturate the main thread (Chrome "site not responding").
 * Every consumer (header unread badge, Messages page) MUST go through this
 * module instead of calling `supabase.channel('messages:...')` directly.
 */
import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type MessageRealtimeEvent = {
  type: 'INSERT' | 'UPDATE';
  /** True when the change targets this company's inbox (incoming message). */
  isIncoming: boolean;
  row: Record<string, any> | null;
};

type Listener = (event: MessageRealtimeEvent) => void;

type Entry = {
  channel: RealtimeChannel;
  listeners: Set<Listener>;
};

const entries = new Map<string, Entry>();

const emit = (companyId: string, event: MessageRealtimeEvent) => {
  const entry = entries.get(companyId);
  if (!entry) return;
  entry.listeners.forEach((listener) => {
    try {
      listener(event);
    } catch (err) {
      console.error('messagesRealtime listener error:', err);
    }
  });
};

const createEntry = (companyId: string): Entry => {
  const channel = supabase
    .channel(`messages:${companyId}`)
    // Scoped to this tenant: an unrelated company's message can never wake this tab.
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'platform_messages',
        filter: `recipient_company_id=eq.${companyId}`,
      },
      (payload) => emit(companyId, { type: 'INSERT', isIncoming: true, row: payload.new ?? null }),
    )
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'platform_messages',
        filter: `sender_company_id=eq.${companyId}`,
      },
      (payload) => emit(companyId, { type: 'INSERT', isIncoming: false, row: payload.new ?? null }),
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'platform_messages',
        filter: `recipient_company_id=eq.${companyId}`,
      },
      (payload) => emit(companyId, { type: 'UPDATE', isIncoming: true, row: payload.new ?? null }),
    )
    .subscribe();

  return { channel, listeners: new Set() };
};

/** Subscribe to this company's message stream. Returns an unsubscribe function. */
export const subscribeToCompanyMessages = (companyId: string, listener: Listener): (() => void) => {
  let entry = entries.get(companyId);
  if (!entry) {
    entry = createEntry(companyId);
    entries.set(companyId, entry);
  }
  entry.listeners.add(listener);

  return () => {
    const current = entries.get(companyId);
    if (!current) return;
    current.listeners.delete(listener);
    if (current.listeners.size === 0) {
      entries.delete(companyId);
      supabase.removeChannel(current.channel);
    }
  };
};

/** Test/debug helper: how many topics this module currently owns. */
export const activeMessageTopics = () => Array.from(entries.keys());
